<template>
  <HeadlessFloatingWithAnchor
    ref="floatingWithAnchor"
    :anchor-reference="reactiveAnchorReference"
    :offset="offset"
    :flip="flip"
    :shift="shift"
    :shift-padding="shiftPadding"
    :auto-update="autoUpdate"
    :placement="placement"
    :is-visible="isVisible"
    :duration="duration"
  >
    <slot />
  </HeadlessFloatingWithAnchor>
</template>

<script setup lang="ts">
import type { HeadlessFloatingWithAnchorProps } from './HeadlessFloatingWithAnchor.vue'

export interface HeadlessTooltipProps extends HeadlessFloatingWithAnchorProps {
  delayEnter?: number
  delayLeave?: number
  hoverableContent?: boolean
}

const props = withDefaults(defineProps<HeadlessTooltipProps>(), {
  offset: 6,
  flip: true,
  shift: true,
  shiftPadding: 8,
  autoUpdate: true,
  delayEnter: 100,
  delayLeave: 150,
  hoverableContent: true,
  duration: '0.15',
})

const emit = defineEmits<{
  (e: 'hover:start'): void
  (e: 'hover:end'): void
}>()

const reactiveAnchorReference = computed(() => props.anchorReference || null)

const isHovered = useElementHover(
  reactiveAnchorReference as unknown as MaybeRefOrGetter<
    EventTarget | null | undefined
  >,
  {
    delayEnter: props.delayEnter,
    delayLeave: props.delayLeave,
  },
)

const floatingWithAnchor = useTemplateRef('floatingWithAnchor')

const floatingElement = computed(() => {
  if (!props.hoverableContent) {
    return null
  }

  const floating = floatingWithAnchor.value?.floating as
    | EventTarget
    | Ref<EventTarget | null | undefined>
    | null
    | undefined

  // We want to hover the actual tooltip DOM node.
  // Depending on how the child expose is read, this can arrive as either the node itself
  // or a Ref that wraps it, so `unref` gives us a consistent event target.
  return (unref(floating) as unknown as EventTarget | null | undefined) || null
})

const isTooltipHovered = useElementHover(
  floatingElement as unknown as MaybeRefOrGetter<
    EventTarget | null | undefined
  >,
)

const isVisible = computed(() => isHovered.value || isTooltipHovered.value)

watch(isVisible, (value) => {
  if (value) {
    emit('hover:start')
  } else {
    emit('hover:end')
  }
})
</script>
